import { CompanyService, NewListDraftService } from '@app/core';
import { Router } from '@angular/router';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  type SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { DynamicList, ListType, filterSummary } from '@app/model';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-list-editor',
  templateUrl: './list-editor.component.html',
  styleUrls: ['./list-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ListEditorComponent implements OnChanges {
  private changeDetector = inject(ChangeDetectorRef);
  private companyService = inject(CompanyService);
  private translate = inject(TranslateService);
  private router = inject(Router);
  private drafts = inject(NewListDraftService);

  @Input() list: ListType | null = null;
  @Input() editorActive = false;
  /**
   * A new list starts as a dynamic list with this filter, e.g. the filter of
   * the search ("Save as dynamic list"). Nothing is created before saving.
   */
  @Input() initialFilter: unknown[] | null = null;
  @Output() editorActiveChange = new EventEmitter<boolean>();
  @ViewChild('form') form?: NgForm;

  /** Field with the keyboard focus; its validation message is shown. */
  public focusedField: string | null = null;
  private submitted = false;

  public isSolid = {
    bell: false,
    star: false,
  };
  public isDynamicList = false;
  public filter: unknown[] | null = null;

  /** The filter of a search saved as list, in words instead of the form. */
  public summary: string | null = null;
  public description = '';
  public name = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (this.editorActive && (changes['editorActive'] || changes['list'])) {
      this.submitted = false;
      if (this.list !== null) {
        this.description = this.list.description || '';
        this.name = this.list.name;
        this.isDynamicList = this.list.type === 'dynamic';
        if (this.isDynamicList) {
          this.filter = (this.list as DynamicList).filter;
        }
        this.isSolid.bell = !!this.list.has_alerts;
        this.isSolid.star = !!this.list.is_favorite;
      } else {
        this.description = '';
        this.name = '';
        this.isDynamicList = !!this.initialFilter?.length;
        this.filter = this.initialFilter?.length ? this.initialFilter : null;
        this.summary = null;
        if (this.filter) {
          const filter = this.filter;
          this.companyService.subsectors$.pipe(take(1)).subscribe((sectors) => {
            this.summary = filterSummary(
              filter,
              new Map(sectors.map((x) => [x.id, x.name])),
              (key) => this.translate.instant(key)
            );
            this.changeDetector.markForCheck();
          });
        }
        this.isSolid.bell = false;
        this.isSolid.star = false;
      }
    }
  }

  /**
   * A required rule: an empty or blank value is invalid, shown after the field
   * was edited or a save was attempted.
   */
  isInvalid(field: 'name' | 'description') {
    const dirty = !!this.form?.controls[field]?.dirty;
    return !this[field].trim() && (dirty || this.submitted);
  }

  /**
   * A new dynamic list without a filter yet: the filter is set on the search
   * page, which saves the list.
   */
  get definesFilterNext() {
    return !this.list && this.isDynamicList && !this.initialFilter?.length;
  }

  saveList = () => {
    this.submitted = true;
    if (this.name.trim() && this.description.trim()) {
      if (this.definesFilterNext) {
        this.drafts.draft = {
          name: this.name.trim(),
          description: this.description.trim(),
          favourite: this.isSolid.star,
          alerts: this.isSolid.bell,
        };
        this.closeEditor();
        this.router.navigate(['/search'], {
          queryParams: { mode: 'new-list' },
        });
      } else if (this.list) {
        // edit mode
        this.companyService
          .updateList(this.list.id, {
            description: this.description,
            name: this.name,
            filter: this.isDynamicList ? this.filter : undefined,
          })
          .subscribe(() => this.closeEditor());
      } else if (this.name.length && this.description.length) {
        // new list mode
        this.companyService
          .createCompanyList(
            this.name,
            this.isDynamicList ? 'dynamic' : 'manual',
            this.description,
            this.isSolid.star,
            this.isSolid.bell,
            this.isDynamicList ? this.filter : undefined
          )
          .subscribe(() => this.closeEditor());
      }
    }
  };

  closeEditor() {
    this.editorActive = false;
    // Also called when a save request ends.
    this.changeDetector.markForCheck();
    this.editorActiveChange.emit(this.editorActive);
  }
}
